<script setup lang="ts">
/**
 * UsersListView — `/platform/users` route.
 *
 * Orchestrates the IAM users surface: the table on the page, plus
 * five overlay components for create/edit/permissions/reset/delete.
 * Each overlay is opened with a row's user as context; the parent
 * component owns the open-state and the user being edited.
 *
 * The audit's "drawers and modals over a single list view" decision
 * lives here. We don't navigate away to per-user routes — the list
 * is always behind the overlay.
 */
import { ref, computed } from 'vue'
import { Plus } from 'lucide-vue-next'
import PageHeader from '@/components/ui/PageHeader.vue'
import Card from '@/components/ui/Card.vue'
import FilterRow from '@/components/ui/FilterRow.vue'
import Input from '@/components/ui/Input.vue'
import Button from '@/components/ui/Button.vue'
import Chip from '@/components/ui/Chip.vue'
import ColumnVisibilityMenu from '@/components/ui/ColumnVisibilityMenu.vue'
import { Search } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'

import UsersTable from '../components/UsersTable.vue'
import CreateUserDrawer from '../components/CreateUserDrawer.vue'
import EditUserDrawer from '../components/EditUserDrawer.vue'
import PermissionsDrawer from '../components/PermissionsDrawer.vue'
import ResetPasswordModal from '../components/ResetPasswordModal.vue'
import DeleteUserModal from '../components/DeleteUserModal.vue'
import { useUsersQuery } from '../queries'
import {
  COLUMN_MENU_DEFS,
  DEFAULT_VISIBLE,
  LOCKED_COLUMNS,
} from '../users-table-columns'
import type { UserView } from '../api'

const { t } = useI18n()
const auth = useAuthStore()

const users = useUsersQuery()

// -----------------------------------------------------------------
// Filters
// -----------------------------------------------------------------

type FilterMode = 'all' | 'internal' | 'external'

const filter = ref<FilterMode>('all')
const search = ref('')

const filteredUsers = computed<UserView[]>(() => {
  const all = users.data.value ?? []
  const term = search.value.trim().toLowerCase()
  return all.filter((u) => {
    if (filter.value === 'internal' && u.external) return false
    if (filter.value === 'external' && !u.external) return false
    if (term && !u.userName.toLowerCase().includes(term)) return false
    return true
  })
})

// -----------------------------------------------------------------
// Column visibility
// -----------------------------------------------------------------

const visibleColumns = ref<string[]>([...DEFAULT_VISIBLE])
const columnMenuItems = computed(() =>
  COLUMN_MENU_DEFS.map((c) => ({ id: c.id, label: t(c.labelKey) })),
)

// -----------------------------------------------------------------
// Overlay state
// -----------------------------------------------------------------

const createOpen = ref(false)
const editOpen = ref(false)
const permissionsOpen = ref(false)
const resetOpen = ref(false)
const deleteOpen = ref(false)
const activeUser = ref<UserView | null>(null)

function onEdit(u: UserView) {
  activeUser.value = u
  editOpen.value = true
}
function onPermissions(u: UserView) {
  activeUser.value = u
  permissionsOpen.value = true
}
function onResetPassword(u: UserView) {
  activeUser.value = u
  resetOpen.value = true
}
function onDelete(u: UserView) {
  activeUser.value = u
  deleteOpen.value = true
}
</script>

<template>
  <div>
    <PageHeader
      :title="$t('iam.title')"
      :subtitle="$t('iam.subtitle')"
    >
      <template #actions>
        <Button
          v-if="auth.hasPrivilege('add_users')"
          kind="primary"
          :icon="Plus"
          @click="createOpen = true"
        >
          {{ $t('iam.create.trigger') }}
        </Button>
      </template>
    </PageHeader>

    <Card flush>
      <FilterRow>
        <template #chips>
          <Chip
            :variant="filter === 'all' ? 'active' : 'default'"
            :count="users.data.value?.length ?? 0"
            clickable
            @click="filter = 'all'"
          >{{ $t('iam.filter.all') }}</Chip>
          <Chip
            :variant="filter === 'internal' ? 'active' : 'default'"
            :count="users.data.value?.filter((u) => !u.external).length ?? 0"
            clickable
            @click="filter = 'internal'"
          >{{ $t('iam.filter.internal') }}</Chip>
          <Chip
            :variant="filter === 'external' ? 'active' : 'default'"
            :count="users.data.value?.filter((u) => u.external).length ?? 0"
            clickable
            @click="filter = 'external'"
          >{{ $t('iam.filter.external') }}</Chip>
        </template>

        <template #search>
          <Input
            v-model="search"
            :icon="Search"
            size="sm"
            :placeholder="$t('iam.search.placeholder')"
          />
        </template>

        <template #actions>
          <ColumnVisibilityMenu
            :columns="columnMenuItems"
            v-model:visible="visibleColumns"
            :locked-ids="[...LOCKED_COLUMNS]"
          />
        </template>
      </FilterRow>

      <UsersTable
        :data="filteredUsers"
        :visible="visibleColumns"
        :loading="users.isLoading.value"
        @edit="onEdit"
        @permissions="onPermissions"
        @reset-password="onResetPassword"
        @delete="onDelete"
      />
    </Card>

    <!-- Overlays -->
    <CreateUserDrawer v-model:open="createOpen" />
    <EditUserDrawer v-model:open="editOpen" :user="activeUser" />
    <PermissionsDrawer v-model:open="permissionsOpen" :user="activeUser" />
    <ResetPasswordModal v-model:open="resetOpen" :user="activeUser" />
    <DeleteUserModal v-model:open="deleteOpen" :user="activeUser" />
  </div>
</template>
